import { Badge } from "@/components/design-system/badge";
import ExportDropdown, {
  ExportOption,
} from "@/components/design-system/export-dropdown";
import ListHeader from "@/components/design-system/list-header";
import ListPagination from "@/components/design-system/list-pagination";
import {
  Table,
  type TableColumn,
  type TableSortConfig,
} from "@/components/design-system/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  deleteSchool,
  fetchSchools,
  selectNoSchools,
  selectSchoolActionLoading,
  selectSchoolLoading,
  selectSchools,
  selectTotalItems,
  updateSchool,
} from "@/store/slices/schoolSlice";
import { AppDispatch } from "@store";
import {
  EllipsisVertical,
  Eye,
  GraduationCap,
  ShieldX,
  SquarePen,
  ToggleLeft,
  ToggleRight,
  Trash2,
  User,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";

// Using the SchoolModel from types
import { Button } from "@/components/design-system/button";
import CourseTermAccessModal from "@/components/design-system/CourseTermAccessModal";
import Modal from "@/components/design-system/Modal";
import RestrictionModal, {
  RestrictionFormData,
} from "@/components/design-system/RestrictionModal";
import { downloadSchoolService } from "@/service/school.service";
import { selectUserRole } from "@/store/slices/authSlice";
import { DownloadSchoolQuery, SchoolModel } from "@/types/school.model";
import {
  CSVIcon,
  downloadFileFromBlob,
  ExcelIcon,
  FileFormats,
  hasMenuPermission,
  hasRolePermissions,
  PDFIcon,
  ROLE_PERMISSIONS,
  ROLES,
  successToast,
} from "@/utils/constants";
import { CourseTermAccessIcon } from "@/utils/svg";
import { isNumber } from "lodash";

// Extended interface for UI display purposes
interface SchoolDisplay extends SchoolModel {
  sno?: number;
  status?: "active" | "inactive";
  admin_name?: string;
  email?: string;
  phone?: string;
  no_of_student_licence?: number;
  students_count?: number;
  no_of_teacher_licence?: number;
  teachers_count?: number;
}

// Removed static schoolsData as we'll fetch from API

const ITEMS_PER_PAGE = 10; // Or any other value you prefer

export default function SchoolListSection() {
  const [searchValue, setSearchValue] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [fromDate, setFromDate] = useState<Date | undefined>(undefined);
  const [toDate, setToDate] = useState<Date | undefined>(undefined);
  const [sortConfig, setSortConfig] = useState<TableSortConfig | undefined>(
    undefined
  );

  const dispatch = useDispatch<AppDispatch>();
  const schools = useSelector(selectSchools);
  const loading = useSelector(selectSchoolLoading);
  const actionLoading = useSelector(selectSchoolActionLoading);
  const totalItems = useSelector(selectTotalItems);
  const userRole = useSelector(selectUserRole);
  const noSchools = useSelector(selectNoSchools);
  const userPerms = userRole?.permissions as string[];
  const isSuperAdmin = userRole?.code === ROLES.SUPER_ADMIN;
  const canManageCourseTermAccess =
    isSuperAdmin &&
    hasMenuPermission(ROLE_PERMISSIONS.ACTION_TERM_RESTRICTION, userPerms);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [schoolToDelete, setSchoolToDelete] = useState<string | null>(null);

  const [activateModalOpen, setActivateModalOpen] = useState(false);
  const [restrictionModalOpen, setRestrictionModalOpen] = useState(false);

  const [restrictionFormData, setRestrictionFormData] =
    useState<RestrictionFormData>({
      is_active: false,
      restriction_type: "HOURS",
      hours: "",
      days: {},
      time_ranges: {},
      school: "",
    });

  const [schoolToToggle, setSchoolToToggle] = useState<{
    id: string;
    is_active: boolean;
  } | null>(null);

  const [coursesModalOpen, setCoursesModalOpen] = useState(false);
  const [coursesModalSchoolId, setCoursesModalSchoolId] = useState<
    string | null
  >(null);

  const navigate = useNavigate();

  const handleOpenCoursesModal = useCallback((schoolId: string) => {
    setCoursesModalSchoolId(schoolId);
    setCoursesModalOpen(true);
  }, []);

  const handleCloseCoursesModal = useCallback(() => {
    setCoursesModalOpen(false);
    setCoursesModalSchoolId(null);
  }, []);

  // const router = useRouter();

  const columns: TableColumn<SchoolDisplay>[] = [
    {
      key: "sno",
      title: "S.No",
      dataIndex: "sno",
      minWidth: 68,
      maxWidth: 68,
      align: "center",
      render: (_, __, index) => {
        // Calculate S.No based on current page and index
        const sno = (currentPage - 1) * ITEMS_PER_PAGE + index + 1;
        return (
          <div className="text-[#1F1F1F] text-[16px] font-medium text-center">
            {String(sno).padStart(2, "0")}
          </div>
        );
      },
    },
    {
      key: "name",
      title: "School Name",
      dataIndex: "name",
      minWidth: 212,
      maxWidth: 212,
      sortable: true,
      render: (_, record) => (
        <div
          className="text-[#1F1F1F] text-[16px] font-medium"
          style={{
            overflowWrap: "anywhere",
            whiteSpace: "pre-wrap",
          }}
        >
          {record.name}
        </div>
      ),
    },
    {
      key: "no_of_student_licence",
      title: "Student License Count",
      minWidth: 220,
      maxWidth: 220,
      sortable: true,
      sortKey: "no_of_student_licence",
      render: (_, record) => (
        <div
          className="text-[#1F1F1F] text-[16px] font-medium"
          style={{
            overflowWrap: "anywhere",
            whiteSpace: "pre-wrap",
          }}
        >
          {isNumber(record.no_of_student_licence) ? record.no_of_student_licence : "-"}
        </div>
      ),
    },
    {
      key: "students_count",
      title: "No. of Students",
      minWidth: 187,
      maxWidth: 187,
      sortable: true,
      sortKey: "students_count",
      render: (_, record) => (
        <div
          className="text-[#1F1F1F] text-[16px] font-medium"
          style={{
            overflowWrap: "anywhere",
            whiteSpace: "pre-wrap",
          }}
        >
          {isNumber(record.students_count) ? record.students_count : "-"}
        </div>
      ),
    },
    {
      key: "no_of_teacher_licence",
      title: "Teacher License Count",
      minWidth: 220,
      maxWidth: 220,
      sortable: true,
      sortKey: "no_of_teacher_licence",
      render: (_, record) => (
        <div
          className="text-[#1F1F1F] text-[16px] font-medium"
          style={{
            overflowWrap: "anywhere",
            whiteSpace: "pre-wrap",
          }}
        >
          {isNumber(record.no_of_teacher_licence) ? record.no_of_teacher_licence : "-"}
        </div>
      ),
    },
    {
      key: "teachers_count",
      title: "No. of Teachers",
      minWidth: 187,
      maxWidth: 187,
      sortable: true,
      sortKey: "teachers_count",
      render: (_, record) => (
        <div
          className="text-[#1F1F1F] text-[16px] font-medium"
          style={{
            overflowWrap: "anywhere",
            whiteSpace: "pre-wrap",
          }}
        >
          {isNumber(record.teachers_count) ? record.teachers_count : "-"}
        </div>
      ),
    },
    {
      key: "admin_name",
      title: "School Admin Name",
      minWidth: 222,
      maxWidth: 222,
      sortable: true,
      render: (_, record) => (
        <div
          className="text-[#1F1F1F] text-[16px] font-medium"
          style={{
            overflowWrap: "anywhere",
            whiteSpace: "pre-wrap",
          }}
        >
          {record.user?.name || "N/A"}
        </div>
      ),
    },
    {
      key: "email",
      title: "Email Address",
      minWidth: 249,
      maxWidth: 249,
      sortable: true,
      render: (_, record) => (
        <div
          className="text-[#1F1F1F] text-[16px] font-medium"
          style={{
            overflowWrap: "anywhere",
            whiteSpace: "pre-wrap",
          }}
        >
          {record.user?.email || "N/A"}
        </div>
      ),
    },
    {
      key: "phone",
      title: "Contact Number",
      minWidth: 187,
      maxWidth: 187,
      sortable: true,
      render: (_, record) => (
        <div className="text-[#1F1F1F] text-[16px] font-medium">
          {record.user?.contact_number || "N/A"}
        </div>
      ),
    },
    {
      key: "status",
      title: "Active/Inactive",
      minWidth: 178,
      maxWidth: 178,
      align: "center",
      sortable: true,
      render: (_, record) => (
        <Badge variant={record.is_active ? "success" : "danger"}>
          {record.is_active ? "Active" : "Inactive"}
        </Badge>
      ),
    },
  ];

  if (
    hasRolePermissions(userRole?.permissions as string[], [
      ROLE_PERMISSIONS.ACTION_VIEW_SCHOOL,
      ROLE_PERMISSIONS.ACTION_EDIT_SCHOOL,
      ROLE_PERMISSIONS.ACTION_DELETE_SCHOOL,
      ROLE_PERMISSIONS.ACTION_RESTRICTION_SCHOOL,
      ROLE_PERMISSIONS.ACTION_ACTIVE_INACTIVE_SCHOOL,
    ])
  ) {
    columns.push({
      key: "action",
      title: "Action",
      minWidth: 230,
      maxWidth: 230,
      align: "left",
      fixed: "right",
      render: (_, record) => (
        <div className="flex items-center justify-start space-x-2">
          {hasMenuPermission(
            ROLE_PERMISSIONS.ACTION_VIEW_SCHOOL,
            userRole?.permissions as string[]
          ) && (
              <button
                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                title="View"
                onClick={() =>
                  navigate(`/school-management/view-school/${record.id}`)
                }
              >
                <Eye className="w-6 h-6 text-[#1C1B1F]" />
              </button>
            )}

          {hasMenuPermission(
            ROLE_PERMISSIONS.ACTION_EDIT_SCHOOL,
            userRole?.permissions as string[]
          ) && (
              <button
                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                title="Edit"
                onClick={() =>
                  navigate(`/school-management/edit-school/${record.id}`)
                }
              >
                <SquarePen className="w-6 h-6 text-[#1C1B1F]" />
              </button>
            )}

          {hasMenuPermission(
            ROLE_PERMISSIONS.ACTION_DELETE_SCHOOL,
            userRole?.permissions as string[]
          ) && (
              <button
                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                title="Delete"
                onClick={() => {
                  setSchoolToDelete(record.id);
                  setDeleteModalOpen(true);
                }}
              >
                <Trash2 className="w-6 h-6 text-[#1C1B1F]" />
              </button>
            )}

          {hasRolePermissions(userRole?.permissions as string[], [
            ROLE_PERMISSIONS.ACTION_ACTIVE_INACTIVE_SCHOOL,
            ROLE_PERMISSIONS.ACTION_RESTRICTION_SCHOOL,
          ]) && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="p-1 hover:bg-gray-100 rounded-full transition-colors">
                    <EllipsisVertical className="w-6 h-6 text-[#1C1B1F]" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className="min-w-[180px] !p-0 rounded-[8px] border border-[#EEE] bg-[#FFF] shadow-[0_30px_40px_0_rgba(0,0,0,0.15)]"
                >
                  {hasMenuPermission(
                    ROLE_PERMISSIONS.ACTION_ACTIVE_INACTIVE_SCHOOL,
                    userRole?.permissions as string[]
                  ) && (
                      <DropdownMenuItem
                        className="h-[42px] px-4 py-0 flex items-center gap-2 text-label2a-d text-borderBlack cursor-pointer hover:bg-[#5C88C5] hover:text-white group border-b rounded-none border-[#EDEDED] [&_svg]:!size-6"
                        onClick={() => {
                          setSchoolToToggle({
                            id: record.id,
                            is_active: !record.is_active,
                          });
                          setActivateModalOpen(true);
                        }}
                      >
                        <div className="flex items-center justify-between gap-2 w-full">
                          {record.is_active ? (
                            <>
                              <span>Deactivate</span>
                              <ToggleLeft className="w-6 h-6 text-[#FF152C]" />
                            </>
                          ) : (
                            <>
                              <span>Activate</span>
                              <ToggleRight className="w-6 h-6 text-[#00C41D]" />
                            </>
                          )}
                        </div>
                      </DropdownMenuItem>
                    )}

                  {hasMenuPermission(
                    ROLE_PERMISSIONS.ACTION_RESTRICTION_SCHOOL,
                    userRole?.permissions as string[]
                  ) && (
                      <DropdownMenuItem
                        className="flex items-center gap-2 text-label2a-d text-borderBlack cursor-pointer hover:bg-[#5C88C5] hover:text-white group"
                        onClick={() => handleRestrictionClick(record)}
                      >
                        <div className="flex items-center justify-between gap-2 w-full">
                          <span>Restriction</span>
                          <ShieldX className="w-4 h-4" />
                        </div>
                      </DropdownMenuItem>
                    )}

                  <DropdownMenuItem
                    className="flex items-center gap-2 text-label2a-d text-borderBlack cursor-pointer hover:bg-[#5C88C5] hover:text-white group"
                    onClick={() => {
                      sessionStorage.setItem("schoolBackURL", location?.hash?.replace('#', ''));
                      navigate(`/user-management/teacher?schoolId=${record?.id}`);
                    }}
                  >
                    <div className="flex items-center justify-between gap-2 w-full">
                      <span>Manage Teacher</span>
                      <GraduationCap className="w-4 h-4" />
                    </div>
                  </DropdownMenuItem>

                  <DropdownMenuItem
                    className="flex items-center gap-2 text-label2a-d text-borderBlack cursor-pointer hover:bg-[#5C88C5] hover:text-white group"
                    onClick={() => {
                      sessionStorage.setItem("schoolBackURL", location?.hash?.replace('#', ''));
                      navigate(`/user-management/student?schoolId=${record?.id}`)
                    }}
                  >
                    <div className="flex items-center justify-between gap-2 w-full">
                      <span>Manage Student</span>
                      <User className="w-4 h-4" />
                    </div>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}

          {canManageCourseTermAccess && (
            <button
              className="p-1 hover:bg-gray-100 rounded-full transition-colors"
              title="Courses"
              type="button"
              onClick={() => handleOpenCoursesModal(record.id)}
            >
              <CourseTermAccessIcon />
            </button>
          )}
        </div>
      ),
    });
  }

  // Handle delete school
  const handleDeleteSchool = () => {
    if (schoolToDelete) {
      dispatch(deleteSchool(schoolToDelete))
        .unwrap()
        .then(() => {
          setDeleteModalOpen(false);
          setSchoolToDelete(null);
        })
        .catch((error) => {
          console.log("Failed to delete School:", error);
          // Modal stays open on error
        });
    }
  };

  // Handle activate/deactivate school
  const handleToggleActivation = () => {
    if (schoolToToggle) {
      const { id, is_active } = schoolToToggle;
      dispatch(
        updateSchool({
          id,
          schoolData: { is_active },
        })
      )
        .unwrap()
        .then(() => {
          setActivateModalOpen(false);
          setSchoolToToggle(null);

          dispatch(
            fetchSchools({ page: currentPage, pageSize: ITEMS_PER_PAGE })
          );

          successToast("School status updated successfully", "");
        })
        .catch((error) => {
          console.log("Failed to update school status:", error);
          // Modal stays open on error
        });
    }
  };

  // Handle export of time entries as Excel or PDF
  const handleExport = async (type: "xl" | "pdf" | "csv") => {
    try {
      // Create query params from current filters
      const queryParams: DownloadSchoolQuery = {
        search: searchValue || undefined,
        fromDate: fromDate,
        toDate: toDate,
      };

      const response = await downloadSchoolService(type, queryParams);

      downloadFileFromBlob(response, type, "school_report");

      successToast(
        "Export Successful",
        `Schools exported as ${FileFormats[type].name} successfully`
      );
    } catch (error) {
      console.error(`Error exporting schools as ${type}:`, error);
    }
  };

  const exportOptions: ExportOption[] = [
    {
      label: "PDF",
      icon: <PDFIcon />,
      action: () => handleExport("pdf"),
    },
    {
      label: "Excel",
      icon: <ExcelIcon />,
      action: () => handleExport("xl"),
    },
    {
      label: "CSV",
      icon: <CSVIcon />,
      action: () => handleExport("csv"),
    },
  ];

  // Handle date range changes from the ListHeader component
  const handleDateRangeChange = (
    startDate: Date | undefined,
    endDate: Date | undefined
  ) => {
    setFromDate(startDate);
    setToDate(endDate);
    // Reset to first page when date range changes
    if (currentPage !== 1) {
      setCurrentPage(1);
      // The page change will trigger a fetch
    }
  };

  // Handle sorting when column headers are clicked
  const handleSort = (config: TableSortConfig | null) => {
    setSortConfig(config || undefined);
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };

  // Map frontend column keys to backend API sorting keys
  const getSortField = (key: string): string => {
    const sortFieldMap: Record<string, string> = {
      name: "NME",
      admin_name: "ANME",
      email: "AEML",
      phone: "CNUM",
      status: "ACT",
      no_of_student_licence: "STLCCNT",
      students_count: "STCNT",
      no_of_teacher_licence: "TELCCNT",
      teachers_count: "TECNT",
    };
    return sortFieldMap[key] || key;
  };

  const handleRestrictionClick = (record: SchoolDisplay) => {
    setRestrictionFormData({
      is_active: record.restriction?.is_active || false,
      restriction_type: record.restriction?.restriction_type || "HOURS",
      hours: record.restriction?.periodic_tables[0]?.hours || "",
      days: record.restriction?.periodic_tables[0]?.days || {},
      time_ranges: record.restriction?.periodic_tables[0]?.time_ranges || {},
      school: record.id,
    });
    setTimeout(() => {
      setRestrictionModalOpen(true);
    }, 500);
  };

  // Fetch schools on component mount and when search, pagination, date range, or sorting changes
  useEffect(() => {
    // Reset to first page when search value changes
    if (searchValue && currentPage !== 1) {
      setCurrentPage(1);
      return; // The page change will trigger another fetch
    }

    // Create an AbortController to cancel duplicate requests
    const abortController = new AbortController();
    const signal = abortController.signal;

    // Prepare sorting parameters if sort config exists
    const sortParams = sortConfig
      ? {
        sortBy: getSortField(sortConfig.key),
      }
      : {};

    // Use a timeout to debounce the API call
    const timeoutId = setTimeout(() => {
      // Only proceed if the request hasn't been aborted
      if (!signal.aborted) {
        dispatch(
          fetchSchools({
            page: currentPage,
            pageSize: ITEMS_PER_PAGE,
            search: searchValue,
            fromDate: fromDate,
            toDate: toDate,
            ...sortParams,
          })
        );
      }
    }, 0); // 0ms delay still puts it in the next event loop cycle

    // Cleanup function to abort any pending requests when dependencies change
    return () => {
      clearTimeout(timeoutId);
      abortController.abort();
    };
  }, [dispatch, currentPage, searchValue, fromDate, toDate, sortConfig]);

  if (noSchools) {
    return (
      <div className="bg-white rounded-[12px] px-4 sm:px-6 md:px-[32px] w-full overflow-hidden">
        <div
          className={`flex flex-col h-[calc(100vh-140px)] mb-2 overflow-x-auto justify-center items-center`}
        >
          {loading ? (
            <>
              <p className="text-[#1F1F1F] text-[24px] font-medium mb-[28px]">
                Loading...
              </p>
            </>
          ) : (
            <>
              <p className="text-[#1F1F1F] text-[24px] font-bold mb-[9px]">
                No Schools Added yet
              </p>
              <p className="text-[#1F1F1F] text-[24px] font-medium mb-[28px]">
                Start by creating your first schools to students
              </p>

              {hasMenuPermission(
                ROLE_PERMISSIONS.BUTTON_ADD_SCHOOL,
                userRole?.permissions as string[]
              ) && (
                  <Button
                    className="w-[195px] h-[48px] bg-[#5C88C5] text-white"
                    onClick={() => navigate("/school-management/add-school")}
                  >
                    Add School
                  </Button>
                )}
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-[12px] px-4 sm:px-6 md:px-[32px] w-full overflow-hidden  h-full flex flex-1 flex-col">
      <ListHeader
        title="School List"
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        onDateRangeChange={handleDateRangeChange}
        rightAction={
          <>
            {/* Export Button */}
            {hasMenuPermission(
              ROLE_PERMISSIONS.BUTTON_EXPORT_SCHOOL,
              userRole?.permissions as string[]
            ) && <ExportDropdown options={exportOptions} />}

            {hasMenuPermission(
              ROLE_PERMISSIONS.BUTTON_ADD_SCHOOL,
              userRole?.permissions as string[]
            ) && (
                <Button
                  variant="primary"
                  size="md"
                  className="w-[161px] h-[48px] bg-[#5C88C5] text-white"
                  onClick={() => navigate("/school-management/add-school")}
                >
                  Add School
                </Button>
              )}
          </>
        }
      />

      {/* Table Container with fixed header */}
      <div className="flex flex-1 flex-col border border-[#EDEDED] rounded-md overflow-x-auto">
        <div className="flex-1 overflow-auto h-full flex">
          {/* {loading ? (
            <div className="flex items-center justify-center h-[calc(100vh-300px)] w-full">
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 border-4 border-t-[#5C88C5] border-r-[#5C88C5] border-b-[#5C88C5] border-l-transparent rounded-full animate-spin"></div>
                <p className="mt-4 text-gray-600">Loading schools...</p>
              </div>
            </div>
          ) : ( */}
          <Table
            columns={columns}
            data={schools} // Use data from Redux store
            rowKey="id"
            showHeader={true} // Hide header as we're showing it separately above
            size="middle"
            sticky
            scroll={{ x: 1200 }}
            loading={loading} // We're handling loading state ourselves
            // className="body-only-table"
            sortConfig={sortConfig}
            onSort={handleSort}
          />
          {/* )} */}
        </div>
      </div>

      <ListPagination
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        totalItems={totalItems} // Use actual total items from data
        itemsPerPage={ITEMS_PER_PAGE}
        itemsLabel="Schools"
      />

      {/* Delete School Confirmation Modal */}
      <Modal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Delete School"
        width="728px"
        contentClassName="p-0"
      >
        <div className="p-6">
          <p className="text-[#7B7B7B] text-[24px] mb-8">
            Are you sure want to Delete School ?
          </p>
        </div>
        <div className="flex justify-end border-t border-gray-200 p-4 gap-4">
          <Button
            variant="light"
            size="md"
            className="w-[120px] h-[48px] bg-[#fff] text-dark border-[1px] border-[#5C88C5]"
            onClick={() => setDeleteModalOpen(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="md"
            className="w-[142px] h-[48px] bg-[#5C88C5] text-white"
            onClick={() => handleDeleteSchool()}
            disabled={actionLoading}
          >
            {actionLoading ? "Deleting..." : "Yes, Delete"}
          </Button>
        </div>
      </Modal>

      {/* Activate/Deactivate School Confirmation Modal */}
      <Modal
        isOpen={activateModalOpen}
        onClose={() => setActivateModalOpen(false)}
        title={schoolToToggle?.is_active ? "Activate" : "Deactivate"}
        width="728px"
        contentClassName="p-0"
      >
        <div className="p-6">
          <p className="text-[#7B7B7B] text-[24px] mb-8">
            {schoolToToggle?.is_active ? (
              <>Are you sure you want to activate the school?</>
            ) : (
              <>
                Are you sure you want to deactivate the school?
                <br />
                Once deactivated, teacher and student credentials will no longer
                work.
              </>
            )}
          </p>
        </div>
        <div className="flex justify-end border-t border-gray-200 p-4 gap-4">
          <Button
            type="button"
            variant="light"
            size="md"
            className="w-[120px] h-[48px] bg-[#fff] text-dark border-[1px] border-[#5C88C5]"
            onClick={() => setActivateModalOpen(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            size="md"
            className="w-[142px] h-[48px] bg-[#5C88C5] text-white"
            onClick={() => handleToggleActivation()}
            disabled={actionLoading}
          >
            {actionLoading
              ? "Processing..."
              : schoolToToggle?.is_active
                ? "Yes, Activate"
                : "Yes, Deactivate"}
          </Button>
        </div>
      </Modal>

      <CourseTermAccessModal
        isOpen={coursesModalOpen}
        onClose={handleCloseCoursesModal}
        schoolId={coursesModalSchoolId}
      />

      {/* restriction school module */}
      <RestrictionModal
        initialData={restrictionFormData}
        onRefresh={() => {
          dispatch(
            fetchSchools({
              page: currentPage,
              pageSize: ITEMS_PER_PAGE,
              search: searchValue,
              fromDate: fromDate,
              toDate: toDate,
              sortBy: sortConfig?.key,
            })
          );
        }}
        isOpen={restrictionModalOpen}
        onClose={() => {
          setRestrictionFormData({
            is_active: false,
            restriction_type: "HOURS",
            hours: "",
            days: {},
            time_ranges: {},
            school: "",
          });
          setRestrictionModalOpen(false);
        }}
        schoolId={restrictionFormData?.school}
        isEditMode={restrictionFormData?.is_active}
      />
    </div>
  );
}
